import { pricingConfig, warrantyConfig } from '../data/pricing';
import type { Bundle, BundleId, WarrantyTier } from '../data/pricing';
import type { CartLine, Lang } from '../types/cart';

const round = (n: number) => Math.round(n * 100) / 100;
const promoFactor = () =>
  pricingConfig.promo.active ? 1 - pricingConfig.promo.pctOff / 100 : 1;

export function formatMoney(n: number, lang: Lang = 'en') {
  return new Intl.NumberFormat(lang === 'es' ? 'es-US' : 'en-US', {
    style: 'currency',
    currency: pricingConfig.currency,
  }).format(n);
}

export function volumeDiscountPct(qty: number) {
  let pct = 0;
  for (const t of pricingConfig.volumeTiers) if (qty >= t.minQty) pct = t.discountPct;
  return pct;
}

export function priceIndividual(qty: number) {
  const list = round(qty * pricingConfig.unitPrice);
  const pct = volumeDiscountPct(qty);
  const unit = round(pricingConfig.unitPrice * (1 - pct / 100) * promoFactor());
  const total = round(unit * qty);
  return { qty, list, pct, unit, total, savings: round(list - total) };
}

export function getBundle(id: BundleId): Bundle {
  const b = pricingConfig.bundles.find((x) => x.id === id);
  if (!b) throw new Error(`Unknown bundle ${id}`);
  return b;
}

export function priceBundle(b: Bundle) {
  const total = round(b.price * promoFactor());
  const list = round(b.qty * pricingConfig.unitPrice);
  return {
    total,
    list,
    perUnit: round(total / b.qty),
    savings: round(list - total),
    savingsPct: Math.round(((list - total) / list) * 100),
  };
}

/** Lowest per-light price across all offers. */
export function lowestPerLight() {
  return Math.min(...pricingConfig.bundles.map((b) => priceBundle(b).perUnit));
}

/** Entry price used for "Starting at $X" anchors. */
export function startingPrice() {
  return round(pricingConfig.unitPrice * promoFactor());
}

export function addonQualifies(id: BundleId) {
  return pricingConfig.addon.qualifyingBundles.includes(id);
}

export function addonUnitPrice() {
  return round(pricingConfig.addon.price * promoFactor());
}

export function priceSystem(id: BundleId, extras: number) {
  const bundle = getBundle(id);
  const base = priceBundle(bundle);
  const ex = addonQualifies(id) ? Math.min(extras, pricingConfig.addon.maxQty) : 0;
  const extrasTotal = round(ex * addonUnitPrice());
  const extrasList = round(ex * pricingConfig.unitPrice);
  return {
    bundle,
    base,
    extras: ex,
    extrasTotal,
    extrasSavings: round(extrasList - extrasTotal),
    lights: bundle.qty + ex,
    total: round(base.total + extrasTotal),
    savings: round(base.savings + extrasList - extrasTotal),
  };
}

export function upgradeTarget(id: BundleId): Bundle | null {
  const next = pricingConfig.upgradePaths[id];
  return next ? getBundle(next) : null;
}

/** Official bundle that delivers at least as many lights for the same or less money. */
export function betterBundleFor(id: BundleId, extras: number) {
  const sys = priceSystem(id, extras);
  const c = pricingConfig.bundles
    .filter((b) => b.id !== id && b.qty >= sys.lights)
    .map((b) => ({ bundle: b, price: priceBundle(b).total }))
    .filter((x) => x.price <= sys.total)
    .sort((a, b) => a.price - b.price)[0];
  if (!c) return null;
  return {
    bundle: c.bundle,
    total: c.price,
    saves: round(sys.total - c.price),
    extraLights: c.bundle.qty - sys.lights,
  };
}

/** Bundle worth suggesting to a Build-Your-Own shopper (costs less than one more light extra). */
export function bundleSuggestionForIndividual(qty: number) {
  const ind = priceIndividual(qty);
  const c = pricingConfig.bundles
    .filter((b) => b.qty > 1 && b.qty >= qty)
    .map((b) => ({ bundle: b, price: priceBundle(b).total }))
    .filter((x) => x.price <= ind.total + pricingConfig.unitPrice)
    .sort((a, b) => a.price - b.price)[0];
  if (!c) return null;
  return {
    bundle: c.bundle,
    total: c.price,
    diff: round(c.price - ind.total),
    extraLights: c.bundle.qty - qty,
  };
}

export function warrantyTierForLine(line: CartLine): WarrantyTier {
  return line.kind === 'system' ? getBundle(line.bundleId).warranty : warrantyConfig.individual;
}

export function warrantyLabel(tier: WarrantyTier, lang: Lang = 'en') {
  const labels = lang === 'es' ? warrantyConfig.labelsEs : warrantyConfig.labels;
  return { label: labels[tier] };
}

export function priceLine(line: CartLine) {
  if (line.kind === 'individual') {
    const p = priceIndividual(line.qty);
    return { lights: line.qty, total: p.total, savings: p.savings };
  }
  const s = priceSystem(line.bundleId, line.extras);
  return { lights: s.lights, total: s.total, savings: s.savings };
}

export function bundleName(b: Bundle, lang: Lang) {
  return lang === 'es' ? b.nameEs : b.name;
}
